/**
 * Divisional Charts Configuration (Varga Charts)
 *
 * Centralized configuration for D7-D60 divisional charts displayed in accordion sections.
 * Note: Muhurta and Nakshatra chakras are configured separately in divisionalChakraConfig.ts
 * and rendered within Basic Panchanga section.
 *
 * Follows Sanskrit authenticity and DRY principles.
 */

export interface DivisionalChart {
    id: string;
    name: string;
    shortName: string;
    vedicName: string;
    description: string;
    tableComponent?: string;
    toggleConfig?: {
        label: string;
        colorScheme: string;
        hookName: string;
    };
}

/**
 * Complete configuration for all divisional charts in the application
 * Ordered by traditional importance and current implementation status
 */
export const DIVISIONAL_CHARTS: DivisionalChart[] = [
    {
        id: 'd7-chara-karaka',
        name: 'D7',
        shortName: 'D7',
        vedicName: 'Chara Karaka',
        description: 'Seven significators system',
        tableComponent: 'CharaKarakaInfoTable',
        toggleConfig: {
            label: 'Show D7 Saptamsa',
            colorScheme: 'cyan',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd8-ashtamsha',
        name: 'D8',
        shortName: 'D8',
        vedicName: 'Ashtamsha Vara',
        description: 'Eight-fold division for detailed analysis',
        tableComponent: 'AshtamshaInfoTable',
        toggleConfig: {
            label: 'Show Ashtamsha Chakra',
            colorScheme: 'orange',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd12-dwadashamsha',
        name: 'D12',
        shortName: 'D12',
        vedicName: 'Lakshmi Dwadashamsha',
        description: 'Twelve-fold division for Lakshmi qualities',
        tableComponent: 'LakshmiDwadashamshaInfoTable',
        toggleConfig: {
            label: 'Show D12 Dwadasamsa',
            colorScheme: 'yellow',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd20-vimsamsha-guruvadi',
        name: 'D20',
        shortName: 'D20',
        vedicName: 'Vimsamsha Guruvadi',
        description: 'Twenty Ayurvedic qualities for constitution analysis',
        tableComponent: 'VimsamshaDivisionsInfoTable',
        toggleConfig: {
            label: 'Show D20 Vimsamsa',
            colorScheme: 'green',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd24-divisions',
        name: 'D24',
        shortName: 'D24',
        vedicName: 'D24',
        description: 'Atri Muni Relatives (family) and Guna Tattva (qualities)',
        tableComponent: 'D24DivisionsTable',
        toggleConfig: {
            label: 'Show D24 Siddhamsa',
            colorScheme: 'blue',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd27-nakshatramsha',
        name: 'D27',
        shortName: 'D27',
        vedicName: 'Nakshatramsha',
        description: 'Twenty-seven nakshatra divisions for character analysis',
        tableComponent: 'NakshatramshaInfoTable'
    },
    {
        id: 'd30-divisions',
        name: 'D30',
        shortName: 'D30',
        vedicName: 'D30',
        description: 'Bhrigu Trimsamsha (spiritual qualities) and Muhurta (time divisions)',
        tableComponent: 'D30DivisionsTable',
        toggleConfig: {
            label: 'Show D30 Trimsamsa',
            colorScheme: 'purple',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd40-divisions',
        name: 'D40',
        shortName: 'D40',
        vedicName: 'D40',
        description: 'Ahirbhutamsha (mantras) and Ashrama Vashita (maternal lineage)',
        tableComponent: 'D40DivisionsTable',
        toggleConfig: {
            label: 'Show D40 Khavedamsa',
            colorScheme: 'pink',
            hookName: 'useDivisionalChakraSettings'
        }
    },
    {
        id: 'd60-karana-shashtyamsha',
        name: 'D60',
        shortName: 'D60',
        vedicName: 'Karana Shashtyamsha',
        description: 'Sixty-fold division for karmic causes',
        tableComponent: 'KaranaShashtyamshaInfoTable',
        toggleConfig: {
            label: 'Show D60 Shashtiamsa',
            colorScheme: 'red',
            hookName: 'useDivisionalChakraSettings'
        }
    }
];

/**
 * Get divisional chart configuration by ID
 */
export const getDivisionalChart = (id: string): DivisionalChart | undefined => {
    return DIVISIONAL_CHARTS.find(chart => chart.id === id);
};

/**
 * Get all divisional charts with toggle functionality
 */
export const getDivisionalChartsWithToggles = (): DivisionalChart[] => {
    return DIVISIONAL_CHARTS.filter(chart => chart.toggleConfig);
};

/**
 * Get all divisional charts with table components
 */
export const getDivisionalChartsWithTables = (): DivisionalChart[] => {
    return DIVISIONAL_CHARTS.filter(chart => chart.tableComponent);
};

/**
 * Map divisional chart ID to chakra type for universal hooks
 * Includes muhurta and nakshatra even though they're not in DIVISIONAL_CHARTS array
 */
export const getChakraTypeFromId = (id: string): string | undefined => {
    const mapping: Record<string, string> = {
        'muhurta-chakra': 'muhurta',
        'nakshatra-chakra': 'nakshatra',
        'd7-chara-karaka': 'd7',
        'd8-ashtamsha': 'd8',
        'd12-dwadashamsha': 'd12',
        'd20-vimsamsha-guruvadi': 'd20',
        'd24-divisions': 'd24',
        'd30-divisions': 'd30',
        'd40-divisions': 'd40',
        'd60-karana-shashtyamsha': 'd60'
    };
    return mapping[id];
};